import React, { FC, memo, useCallback, useEffect } from 'react'

import { useSetFocus } from '@please/lrud'
import { FocusEvent } from '@please/lrud/dist/types'
import { useLocation } from 'react-router-dom'
import { Element, scroller } from 'react-scroll'

import { Loader } from '@/components/Loader'
import { Scrollbar } from '@/components/Scrollbar/Scrollbar'
import { Tile } from '@/components/Tiles/Tile'
import { LineTileShape, LineTileSize } from '@/constants/common'
import { focusIds, orientation } from '@/constants/focus'
import { paths } from '@/constants/paths'
import { ScrollContainer } from '@/containers/styles'
import { OpenTileProps, useOpenTile } from '@/hooks/useOpenTile'
import useStore from '@/store/useStore'
import { TileProps } from '@/types/componentsPropsTypes'
import { TileType } from '@/types/servicesTypes/configTypes'

import { useListController } from './hooks'
import {
  ListElem,
  ListLoader,
  ListRow,
  ListTitle,
  ScrollbarWrapper,
} from './styles'
import { ListType } from './types'

const ListRaw: FC<ListType> = ({
  pageFocusId,
  listContextId,
  isSearchListType = false,
  name,
  listTitle,
  tileShape = LineTileShape.ASPECT16x9,
  searchResultType,
  activeTileId,
  activeTileIndex,
  activeColumn,
  setActiveColumn,
  fetchData,
  isListPopup = false,
  isPage = false,
  isCatalogue = false,
  scrollOptions,
  showScrollbar = false,
}) => {
  const { pathname } = useLocation()
  const setFocus = useSetFocus()
  const setPageFocusId = useStore((state) => state.page.setPageFocusId)
  const isEventPage = pathname?.includes(paths.EVENT_PAGE)
  const authPopup = useStore((state) => state.common.authPopup)

  const listFocusId = `${focusIds.LIST}${listContextId}`

  const {
    loading,
    groupedListData,
    listData,
    totalRows,
    lastGridPosition,
    onGridMove,
    onRightMove,
    onMouseOverTile,
  } = useListController({
    fetchData,
    listContextId,
    isSearchListType,
    searchResultType,
    activeTileId,
    activeTileIndex,
    activeColumn,
    setActiveColumn,
    tileShape,
    listFocusId,
    scrollOptions,
  })

  const onHandleToggle = useOpenTile({
    shouldNavigateToEventUrl: isEventPage,
    listContextId,
    isSearchListType,
    name,
    tileShape,
    searchResultType,
  })

  useEffect(() => {
    if (isPage && authPopup) {
      return
    }
    groupedListData && setFocus(pageFocusId)
  }, [groupedListData])

  const handleToggle = useCallback(
    ({ tileData, focusId }: OpenTileProps) => {
      const tileIndex = listData?.findIndex(({ id }) => tileData.id === id)
      onHandleToggle({
        tileData,
        focusId: isCatalogue ? tileData.id : focusId,
        isOpenedFromList: isListPopup,
        tileIndex,
        anchorId: listContextId,
      })
      isPage && setPageFocusId(tileData.id)
    },
    [onHandleToggle, listData],
  )

  const onListFocus = (e: FocusEvent) => {
    scroller.scrollTo(
      `${listFocusId}_row_${e.currentNode.defaultFocusRow}`,
      scrollOptions,
    )
  }

  return (
    groupedListData && (
      <ListElem
        elementType="section"
        defaultFocusColumn={lastGridPosition.columnIndex}
        defaultFocusRow={lastGridPosition.rowIndex}
        focusId={listFocusId}
        onGridMove={onGridMove}
        onRight={onRightMove}
        onFocused={onListFocus}
        isGrid
      >
        <ScrollContainer
          id={scrollOptions.containerId}
          height={isCatalogue ? '100%' : scrollOptions.height}
          $paddingBottom={scrollOptions.paddingBottom}
          $withMargin
        >
          {listTitle && <ListTitle>{listTitle}</ListTitle>}
          {groupedListData.map((row: TileType[], index: number) => {
            const rowFocusId = `${listFocusId}_row_${index}`
            return (
              <Element key={rowFocusId} name={rowFocusId}>
                <ListRow
                  focusId={rowFocusId}
                  className="grid-row"
                  $tileShape={tileShape}
                >
                  {row.map((tile: TileType, i: number) => {
                    const tileFocusId = `${rowFocusId}_${tile.id}_${i}`
                    const tileProps: TileProps = {
                      handleToggle,
                      focusId: tileFocusId,
                      data: tile,
                      broadcastId: '',
                      onMouseOverTile,
                      tileShape,
                      tileSize: LineTileSize.DEFAULT,
                    }
                    return <Tile key={tileFocusId} {...tileProps} />
                  })}
                </ListRow>
              </Element>
            )
          })}
          {loading && (
            <Element key="ListLoader" name="ListLoader">
              <ListLoader $tileShape={tileShape}>
                <Loader />
              </ListLoader>
            </Element>
          )}
        </ScrollContainer>
        {showScrollbar && totalRows > 1 && (
          <ScrollbarWrapper>
            <Scrollbar
              length={totalRows}
              activeIndex={lastGridPosition.rowIndex}
              orientation={orientation.VERTICAL}
            />
          </ScrollbarWrapper>
        )}
      </ListElem>
    )
  )
}

export const List = memo(ListRaw)
